/**
 * Lusty Chic Fit — Product Match panel controller (Phase 7; Size Engine extension).
 * Namespace: window.LustyChicFit (reserved in Phase 1 / CLAUDE.md).
 * Storage key: lustychic_fit_profile (reserved in Phase 1, written by assets/lc-fit-quiz.js).
 *
 * Scores exactly the one product embedded by snippets/lc-fit-product-match.liquid using
 * LustyChicFit.Engine (assets/lc-fit-engine.js) — the identical, unduplicated scoring
 * module the results page uses, so a given profile+product produces byte-identical
 * displayedScore in both places. This file only decides which panel STATE to show and
 * fills in text; it never computes a Fit Match score itself.
 *
 * Size Engine addition (post-V1): once the panel is in the "match" state (a Fit Match
 * score is already showing), this file separately asks LustyChicFit.SizeEngine
 * (assets/lc-size-engine.js) for a size recommendation and renders it in the same panel.
 * The Size Engine call is entirely independent of Fit Match — it never reads, computes,
 * or influences displayedScore/rawCompatibility/qualifies, and a failure in it (missing
 * script, malformed product.sizes, etc.) can never hide or alter the Fit Match content
 * already rendered, because it is wrapped in its own try/catch and evaluated after the
 * Fit Match render is complete (see renderMatch()).
 *
 * Fails safe (7.28): every step is wrapped so a missing Engine, missing/malformed
 * product data, or a broken/unsupported profile shape simply leaves the panel hidden —
 * it never throws in a way that could affect the surrounding product page.
 *
 * UX correction (2026-09-20): leg_shape is now an unlimited multi-select array on the
 * profile (see assets/lc-fit-quiz.js / assets/lc-fit-engine.js). hasAnyAnswer() below was
 * updated to treat it as an array — an empty array is not itself "answered" — matching
 * the identical fix already made in assets/lc-fit-results.js. Nothing else in this file
 * reads leg_shape directly.
 */
window.LustyChicFit = window.LustyChicFit || {};

(function (ns) {
  'use strict';

  // Translated UI strings from snippets/lc-fit-i18n.liquid; falls back to the built-in English.
  var T = function (key, fallback) {
    var d = window.LustyChicFitI18n;
    return d && d[key] ? d[key] : fallback;
  };

  var STORAGE_KEY = 'lustychic_fit_profile';

  var RISE_DISPLAY = { Low: T('rise.Low', 'Low Rise'), Mid: T('rise.Mid', 'Mid Rise'), High: T('rise.High', 'High Rise') };
  var LEG_DISPLAY = { Skinny: T('leg.Skinny', 'Skinny'), Slim: T('leg.Slim', 'Slim'), Straight: T('leg.Straight', 'Straight'), Flare: T('leg.Flare', 'Flare'), Wide: T('leg.Wide', 'Wide Leg') };

  function safeGetProfile() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function hasAnyAnswer(profile) {
    if (!profile) return false;
    return !!(
      profile.body_shape ||
      profile.body_shape_unsure ||
      profile.age_range ||
      profile.height ||
      profile.usualUkSize ||
      profile.rise ||
      (Array.isArray(profile.leg_shape) ? profile.leg_shape.length : profile.leg_shape) ||
      (profile.fit_goals && profile.fit_goals.length)
    );
  }

  function readProductData(root) {
    var node = root.querySelector('[data-lc-fit-product-match-data]');
    if (!node) return null;
    try {
      var data = JSON.parse(node.textContent);
      return data && typeof data === 'object' ? data : null;
    } catch (e) {
      return null;
    }
  }

  function isNonEmptyString(v) {
    return typeof v === 'string' && v.trim() !== '';
  }

  function isDebugRequested() {
    try {
      return new URLSearchParams(window.location.search).get('fitdebug') === '1';
    } catch (e) {
      return false;
    }
  }

  function dispatchFitEvent(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (e) {
      /* no-op */
    }
  }

  function showOnly(root, key) {
    var panels = root.querySelectorAll('[data-lc-fit-pm-invite], [data-lc-fit-pm-lowdata], [data-lc-fit-pm-match]');
    for (var i = 0; i < panels.length; i++) panels[i].hidden = true;
    var target = root.querySelector('[data-lc-fit-pm-' + key + ']');
    if (target) target.hidden = false;
    root.hidden = false;
  }

  function bindLinks(root, product) {
    var quizLinks = root.querySelectorAll('[data-lc-fit-pm-quiz-link]');
    for (var i = 0; i < quizLinks.length; i++) {
      quizLinks[i].addEventListener('click', function () {
        dispatchFitEvent('fit_pdp_quiz_clicked', { productId: product.id });
      });
    }
    var resultsLinks = root.querySelectorAll('[data-lc-fit-pm-results-link]');
    for (var j = 0; j < resultsLinks.length; j++) {
      resultsLinks[j].addEventListener('click', function () {
        dispatchFitEvent('fit_pdp_results_clicked', { productId: product.id });
      });
    }
  }

  function renderLowData(root, product) {
    var fitNoteEl = root.querySelector('[data-lc-fit-pm-lowdata] [data-lc-fit-pm-fitnote]');
    var sizeAdviceEl = root.querySelector('[data-lc-fit-pm-lowdata] [data-lc-fit-pm-sizeadvice]');
    if (fitNoteEl && isNonEmptyString(product.fitNote)) {
      fitNoteEl.textContent = T('fit_prefix', 'Fit — ') + product.fitNote;
      fitNoteEl.hidden = false;
    }
    if (sizeAdviceEl && isNonEmptyString(product.sizeAdvice)) {
      sizeAdviceEl.textContent = T('size_advice_prefix', 'Size advice — ') + product.sizeAdvice;
      sizeAdviceEl.hidden = false;
    }
  }

  function renderSizeBlock(panel, profile, product) {
    var heading = panel.querySelector('[data-lc-fit-pm-size-heading]');
    var valueEl = panel.querySelector('[data-lc-fit-pm-size-value]');
    var reasonEl = panel.querySelector('[data-lc-fit-pm-size-reason]');
    var missingEl = panel.querySelector('[data-lc-fit-pm-size-missing]');
    var otherMatchesEl = panel.querySelector('[data-lc-fit-pm-size-other-matches]');
    [heading, valueEl, reasonEl, missingEl, otherMatchesEl].forEach(function (el) {
      if (el) el.hidden = true;
    });

    var SizeEngine = ns.SizeEngine;
    if (!SizeEngine) return null;

    var result;
    try {
      result = SizeEngine.recommendSize(profile, product);
    } catch (e) {
      return null;
    }

    if (!result || result.status === 'insufficient-product-data') {
      return result || null;
    }

    if (result.status === 'missing-customer-size') {
      if (missingEl) missingEl.hidden = false;
      return result;
    }

    if (result.recommendedSize === null) {
      return result;
    }

    if (result.status === 'recommended-size-not-offered') {
      if (reasonEl && isNonEmptyString(result.reason)) {
        reasonEl.textContent = result.reason;
        reasonEl.hidden = false;
      }
      if (otherMatchesEl) otherMatchesEl.hidden = false;
      return result;
    }

    if (heading) heading.hidden = false;
    if (valueEl) {
      var sizeText = T('suggested_size', 'Suggested size: UK{n}').replace('{n}', result.recommendedSize);
      if (result.status === 'recommended-size-sold-out') {
        sizeText += T('size_sold_out', ' — currently sold out in this style.');
      }
      valueEl.textContent = sizeText;
      valueEl.hidden = false;
    }
    if (reasonEl && isNonEmptyString(result.reason)) {
      reasonEl.textContent = result.reason;
      reasonEl.hidden = false;
    }
    if (result.status === 'recommended-size-sold-out' && otherMatchesEl) {
      otherMatchesEl.hidden = false;
    }

    return result;
  }

  function renderMatch(root, profile, product, score, reasons, isWeak) {
    var panel = root.querySelector('[data-lc-fit-pm-match]');
    if (!panel) return null;

    var scoreEl = panel.querySelector('[data-lc-fit-pm-score]');
    if (scoreEl) scoreEl.textContent = T('match_pct', '{n}% Fit Match').replace('{n}', score.displayedScore);

    var tierEl = panel.querySelector('[data-lc-fit-pm-tierline]');
    var weakTextEl = panel.querySelector('[data-lc-fit-pm-weak-text]');
    if (isWeak) {
      if (tierEl) tierEl.hidden = true;
      if (weakTextEl) weakTextEl.hidden = false;
    } else {
      if (tierEl) {
        tierEl.textContent = score.displayedScore >= 80
          ? T('tier_strong', 'A strong match for your saved fit preferences.')
          : T('tier_good', 'A good match for your saved fit preferences.');
        tierEl.hidden = false;
      }
      if (weakTextEl) weakTextEl.hidden = true;
    }

    var reasonsLabel = panel.querySelector('[data-lc-fit-pm-reasons-label]');
    var reasonsList = panel.querySelector('[data-lc-fit-pm-reasons]');
    if (reasonsList) {
      reasonsList.innerHTML = '';
      if (reasons.length) {
        if (reasonsLabel) reasonsLabel.hidden = false;
        for (var i = 0; i < reasons.length; i++) {
          var li = document.createElement('li');
          var icon = document.createElement('span');
          icon.className = 'lc-fit-result-card__check';
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = '✓';
          li.appendChild(icon);
          li.appendChild(document.createTextNode(reasons[i]));
          reasonsList.appendChild(li);
        }
      } else if (reasonsLabel) {
        reasonsLabel.hidden = true;
      }
    }

    var sizeResult = null;
    try {
      sizeResult = renderSizeBlock(panel, profile, product);
    } catch (e) {
      sizeResult = null;
    }

    var shapeReasonEl = panel.querySelector('[data-lc-fit-pm-shapereason]');
    if (shapeReasonEl) {
      var shapeMatched = score.categories.bodyShape && score.categories.bodyShape.matched;
      if (shapeMatched && isNonEmptyString(product.bodyShapeReason)) {
        shapeReasonEl.textContent = T('shape_reason_prefix', 'Why this shape works — ') + product.bodyShapeReason;
        shapeReasonEl.hidden = false;
      } else {
        shapeReasonEl.hidden = true;
      }
    }

    var fitNoteEl = panel.querySelector('[data-lc-fit-pm-fitnote]');
    if (fitNoteEl) {
      if (isNonEmptyString(product.fitNote)) {
        fitNoteEl.textContent = T('fit_prefix', 'Fit — ') + product.fitNote;
        fitNoteEl.hidden = false;
      } else {
        fitNoteEl.hidden = true;
      }
    }

    var sizeAdviceEl = panel.querySelector('[data-lc-fit-pm-sizeadvice]');
    if (sizeAdviceEl) {
      if (isNonEmptyString(product.sizeAdvice)) {
        sizeAdviceEl.textContent = T('size_advice_prefix', 'Size advice — ') + product.sizeAdvice;
        sizeAdviceEl.hidden = false;
      } else {
        sizeAdviceEl.hidden = true;
      }
    }

    var weakCta = panel.querySelector('[data-lc-fit-pm-weak-cta]');
    var strongCta = panel.querySelector('[data-lc-fit-pm-strong-cta]');
    if (weakCta) weakCta.hidden = !isWeak;
    if (strongCta) strongCta.hidden = isWeak;

    return sizeResult;
  }

  function renderPdpDebugPanel(root, product, profile, sizeResult) {
    var panel = document.createElement('dl');
    panel.setAttribute('data-lc-fit-pm-debug', '');
    panel.style.cssText = 'margin:16px 0;padding:12px;border:1px dashed #999;font:12px/1.5 monospace;max-width:100%;overflow-x:auto;';

    function row(label, value) {
      var dt = document.createElement('dt');
      dt.style.cssText = 'font-weight:bold;margin-top:6px;';
      dt.textContent = label;
      var dd = document.createElement('dd');
      dd.style.margin = '0 0 0 12px';
      dd.textContent = value;
      panel.appendChild(dt);
      panel.appendChild(dd);
    }

    var caption = document.createElement('p');
    caption.style.cssText = 'font-weight:bold;margin:0 0 4px;';
    caption.textContent = 'LC Size Engine debug (?fitdebug=1) — internal QA only, not shown to customers';
    panel.appendChild(caption);

    row('usualUkSize (profile)', profile && typeof profile.usualUkSize === 'number' ? String(profile.usualUkSize) : '(none)');
    if (sizeResult) {
      row('status', String(sizeResult.status));
      row('rule', sizeResult.rule === null ? '(none)' : String(sizeResult.rule));
      row('recommendedSize', sizeResult.recommendedSize === null ? '(none)' : 'UK' + sizeResult.recommendedSize);
      row('variantExists', String(sizeResult.variantExists));
      row('available', String(sizeResult.available));
      if (sizeResult.productFlags) {
        row('productFlags', JSON.stringify(sizeResult.productFlags));
      }
    } else {
      row('result', 'SizeEngine did not return a result (missing script or error)');
    }

    root.appendChild(panel);
  }

  function init(root) {
    try {
      var Engine = ns.Engine;
      if (!Engine) return;

      var product = readProductData(root);
      if (!product) return;

      var profile = safeGetProfile();
      var debug = isDebugRequested();
      var sizeResultForDebug = null;

      if (!hasAnyAnswer(profile)) {
        showOnly(root, 'invite');
        bindLinks(root, product);
        dispatchFitEvent('fit_pdp_panel_viewed', { state: 'invite', productId: product.id });
        if (debug) renderPdpDebugPanel(root, product, profile, null);
        return;
      }

      var score = Engine.scoreProduct(profile, product);

      if (score.availablePoints < Engine.MIN_DATA_POINTS) {
        renderLowData(root, product);
        showOnly(root, 'lowdata');
        bindLinks(root, product);
        dispatchFitEvent('fit_pdp_panel_viewed', { state: 'low_data', productId: product.id });
        if (debug) renderPdpDebugPanel(root, product, profile, null);
        return;
      }

      var isWeak = score.displayedScore < Engine.MIN_MATCH_THRESHOLD;
      var reasons = Engine.getMatchReasons(profile, product, score);
      sizeResultForDebug = renderMatch(root, profile, product, score, reasons, isWeak);
      showOnly(root, 'match');
      bindLinks(root, product);
      dispatchFitEvent('fit_pdp_panel_viewed', {
        state: isWeak ? 'weak_match' : 'strong_match',
        productId: product.id,
        score: score.displayedScore
      });
      if (debug) renderPdpDebugPanel(root, product, profile, sizeResultForDebug);
    } catch (e) {
      /* fails safe */
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('[data-lc-fit-product-match]');
    if (root) init(root);
  });
})(window.LustyChicFit);
